import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { LuArrowRight, LuCheck, LuSparkles, LuHandshake, LuPoundSterling, LuInfo, LuHouse } from 'react-icons/lu'
import { Link } from 'react-router-dom'
import SearchHero from '../components/SearchHero.jsx'
import PropertyCard from '../components/PropertyCard.jsx'
import { usePropertyData } from '../hooks/usePropertyData.js'
import { selectAvailableCities, selectFeaturedProperties } from '../utils/propertySelectors.js'

const HomePage = () => {
  const navigate = useNavigate()
  const { properties, loading, error } = usePropertyData()

  const featured = useMemo(() => selectFeaturedProperties(properties), [properties])
  const cities = useMemo(() => selectAvailableCities(properties), [properties])

  const handleSearch = (filters) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value === 'Any') return
      params.set(key, value)
    })
    navigate({ pathname: '/listings', search: params.toString(), state: { resetScroll: true } })
  }

  return (
    <>
      <Helmet>
        <title>Keyzee — Buy &amp; Sell Homes Privately, No Estate Agents</title>
        <meta name="description" content="Keyzee connects home buyers and sellers directly. Browse owner-listed properties, save favourites, and make offers without estate agent fees." />
        <link rel="canonical" href="https://keyzee.co.uk/" />
        <meta property="og:title" content="Keyzee — Buy &amp; Sell Homes Privately" />
        <meta property="og:description" content="Browse owner-listed properties and sell your home privately with Keyzee. No estate agents. Full control." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://keyzee.co.uk/" />
        <meta property="og:site_name" content="Keyzee" />
        <meta property="og:image" content="https://keyzee.co.uk/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Keyzee — Buy &amp; Sell Homes Privately" />
        <meta name="twitter:description" content="Browse owner-listed properties and sell your home privately with Keyzee. No estate agents. Full control." />
        <meta name="twitter:image" content="https://keyzee.co.uk/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://keyzee.co.uk/#organization',
              name: 'Keyzee',
              url: 'https://keyzee.co.uk',
              description: 'UK property platform connecting buyers and sellers directly — no estate agents, no fees.',
              logo: 'https://keyzee.co.uk/pwa-512.png',
              sameAs: ['https://www.facebook.com/profile.php?id=61590357765354'],
            },
            {
              '@type': 'WebSite',
              '@id': 'https://keyzee.co.uk/#website',
              name: 'Keyzee',
              url: 'https://keyzee.co.uk',
              publisher: { '@id': 'https://keyzee.co.uk/#organization' },
              potentialAction: {
                '@type': 'SearchAction',
                target: {
                  '@type': 'EntryPoint',
                  urlTemplate: 'https://keyzee.co.uk/listings?location={search_term_string}',
                },
                'query-input': 'required name=search_term_string',
              },
            },
          ],
        })}</script>
      </Helmet>
      <SearchHero onSearch={handleSearch} />

      <section className="home-section" aria-label="Featured homes">
        <div className="home-section__header">
          <div>
            <p className="search-hero__eyebrow">Featured</p>
            <h2>Fresh owner-listed homes</h2>
          </div>
          <button className="secondary-button" onClick={() => navigate('/listings')}>
            View all listings
          </button>
        </div>

        {loading ? (
          <p>Loading featured homes…</p>
        ) : error ? (
          <p>We couldn’t load properties right now. Please try again shortly.</p>
        ) : featured.length ? (
          <div className="home-section__grid">
            {featured.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        ) : (
          <div className="listings-empty-state">
            <h2>We're a brand-new platform</h2>
            <p>
              Keyzee has just launched, so there aren't any homes listed yet. Be the first to list yours and get
              seen by early buyers with zero competition.
            </p>
            <button className="primary-button" onClick={() => navigate('/sell')}>
              Be the first to list your home
            </button>
          </div>
        )}
      </section>

      <section className="home-section home-section__grid" aria-label="Buyer insights">
        <article className="insight-card">
          <span className="insight-card__tag">Search hubs</span>
          <h3>Where buyers are focused this week</h3>
          <p>Tap a city to jump into community-sourced listings. These are owner uploads, not estate agent inventory.</p>
          <div className="filter-chips" role="list">
            {loading && <span>Loading cities…</span>}
            {!loading &&
              cities.map((city) => (
                <button
                  key={city}
                  type="button"
                  className="filter-chip"
                  onClick={() => navigate(`/listings?location=${encodeURIComponent(city)}&radius=3`)}
                >
                  {city}
                </button>
              ))}
            {!loading && !cities.length && <span>New locations will appear here shortly.</span>}
          </div>
        </article>

        <article className="insight-card">
          <span className="insight-card__tag">Private profiles</span>
          <h3>Login to unlock direct chats</h3>
          <p>
            Secure authentication keeps seller contact details safe. Once signed in you can swap messages, schedule
            visits, and track offers without an intermediary.
          </p>
        </article>
      </section>

      <section className="seller-callout" aria-label="Seller tools">
        <div className="seller-callout__content">
          <p className="search-hero__eyebrow">For homeowners</p>
          <h2>List privately, keep control</h2>
          <p>
            Upload details once, save drafts while you gather documents, and tap our partner services for photography,
            EPCs, or floorplans without leaving Keyzee.
          </p>
          <ul className="icon-list">
            <li>
              <LuCheck aria-hidden="true" /> Full property form with pricing, tenure, amenities, and media uploads
            </li>
            <li>
              <LuCheck aria-hidden="true" /> Save drafts at any time — resume from any device
            </li>
            <li>
              <LuCheck aria-hidden="true" /> Book vetted photographers, EPC assessors, and floorplan pros
            </li>
          </ul>
          <button className="primary-button" onClick={() => navigate('/sell')}>
            Launch seller workspace
          </button>
        </div>
        <div className="seller-callout__card">
          <span>
            <LuSparkles aria-hidden="true" /> Seller view
          </span>
          <strong>Manage listings, media, and services in one place.</strong>
          <p>Draft status updates &bull; Upload galleries &bull; Track bookings</p>
        </div>
      </section>

      <section className="valuation-callout" aria-label="Free house valuation tool">
        <div className="valuation-callout__copy">
          <p className="search-hero__eyebrow">Free tool</p>
          <h2>What is your home worth?</h2>
          <p>
            Get an instant, data-backed estimate using real Land Registry sold prices. No sign-up, no estate agent
            call — just an honest number in seconds.
          </p>
          <Link to="/house-valuation/" className="primary-button pill-action">
            Get free valuation <LuArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="valuation-callout__stat">
          <LuHouse aria-hidden="true" style={{ fontSize: '1.5rem', color: 'var(--color-gold)' }} />
          <strong>Land Registry</strong>
          <span>Powered by official HM Land Registry sold price data — the same source lenders use</span>
        </div>
      </section>

      <section className="how-we-earn" aria-label="How Keyzee makes money">
        <p className="search-hero__eyebrow">The honest bit</p>
        <h2>So… what's the catch?</h2>
        <p className="how-we-earn__intro">
          There isn't one. Here's exactly how we keep the platform free.
        </p>
        <div className="how-we-earn__grid">
          <div className="how-we-earn__item">
            <LuHandshake aria-hidden="true" />
            <h3>We earn from partner services</h3>
            <p>
              If you choose to book a conveyancer, mortgage broker, removal firm, or EPC assessor
              through Keyzee, we may earn a referral fee. You're never obliged to use them.
            </p>
          </div>
          <div className="how-we-earn__item">
            <LuInfo aria-hidden="true" />
            <h3>Estate agents do the same</h3>
            <p>
              This isn't new — estate agents have always earned referral fees from solicitors,
              surveyors, and mortgage advisors. It's standard practice in the industry.
            </p>
          </div>
          <div className="how-we-earn__item">
            <LuPoundSterling aria-hidden="true" />
            <h3>The difference? We don't charge you on top</h3>
            <p>
              Estate agents earn referral fees <em>and</em> charge you 1–3% commission on your sale.
              We only do the referrals — you keep every pound of your sale price.
            </p>
          </div>
        </div>
      </section>

      <section className="rightmove-crosssell" aria-label="Already on Rightmove?">
        <div className="rightmove-crosssell__card">
          <span><LuSparkles aria-hidden="true" /> Import from Rightmove</span>
          <strong>Paste a URL. We do the rest.</strong>
          <p>Photos, description, price and more — pulled in automatically.</p>
        </div>
        <div className="rightmove-crosssell__content">
          <p className="search-hero__eyebrow">Already on Rightmove?</p>
          <h2>Rightmove listing? Add Keyzee in three minutes</h2>
          <p>
            Paste your Rightmove URL and we'll import your property details automatically.
            Reach serious, finance-ready buyers — with zero agent fees.
          </p>
          <Link to="/also-list/" className="primary-button pill-action">
            See how it works <LuArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <div className="biz-nudge">
        <span>Property professional or business partner?</span>
        <a href="https://business.keyzee.co.uk/" target="_blank" rel="noreferrer" className="biz-nudge__link">
          Visit Keyzee for Business <LuArrowRight aria-hidden="true" />
        </a>
      </div>
    </>
  )
}

export default HomePage
